import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data.data;
          useAuthStore.getState().setAccessToken(accessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch {
          // Refresh failed, logout
          useAuthStore.getState().logout();
        }
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// AUTH API
// ============================================================================

export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateMe: async (data: { firstName?: string; lastName?: string; preferences?: unknown }) => {
    const response = await api.patch('/auth/me', data);
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

// ============================================================================
// TRACKS API
// ============================================================================

export const tracksApi = {
  getLiveTracks: async (params?: {
    minLat?: number;
    maxLat?: number;
    minLng?: number;
    maxLng?: number;
    classification?: string;
  }) => {
    const response = await api.get('/tracks/live', { params });
    return response.data;
  },

  getTracks: async (params?: {
    sensorId?: string;
    classification?: string;
    minConfidence?: number;
    startTime?: string;
    endTime?: string;
    page?: number;
    pageSize?: number;
    // Identity-based search filters
    model?: string;
    manufacturer?: string;
    serialNumber?: string;
    registration?: string;
  }) => {
    const response = await api.get('/tracks', { params });
    return response.data;
  },

  getTrack: async (trackId: string) => {
    const response = await api.get(`/tracks/${trackId}`);
    return response.data;
  },

  getTrackHistory: async (trackId: string, startTime: string, endTime: string, limit?: number) => {
    const response = await api.get(`/tracks/${trackId}/history`, {
      params: { startTime, endTime, limit },
    });
    return response.data;
  },

  getStats: async (startTime: string, endTime: string) => {
    const response = await api.get('/tracks/stats/summary', {
      params: { startTime, endTime },
    });
    return response.data;
  },

  // Flights bucketed by period — powers the "Flights by period" widget on
  // the Pattern Analysis tab (Phase 3 item 18). Server validates the range
  // size per bucket; we let the 400 bubble up so the widget can show a
  // friendly "range too wide" hint rather than silently returning nothing.
  getFlightCounts: async (
    bucket: 'hour' | 'day' | 'week',
    startTime: string,
    endTime: string
  ): Promise<{
    data: Array<{ bucket: string; totalFlights: number; droneFlights: number }>;
    bucket: 'hour' | 'day' | 'week';
  }> => {
    const response = await api.get('/tracks/stats/flight-counts', {
      params: { bucket, startTime, endTime },
    });
    return response.data;
  },
};

// ============================================================================
// MISSIONS API
// ============================================================================

export const missionsApi = {
  list: async (params?: {
    status?: string;
    pilotId?: string;
    startAfter?: string;
    startBefore?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const response = await api.get('/missions', { params });
    return response.data;
  },

  get: async (missionId: string) => {
    const response = await api.get(`/missions/${missionId}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    description?: string;
    route: unknown;
    plannedStartTime: string;
    plannedEndTime: string;
    aircraftType?: string;
    aircraftRegistration?: string;
    pilotId?: string;
  }) => {
    const response = await api.post('/missions', data);
    return response.data;
  },

  update: async (missionId: string, data: unknown) => {
    const response = await api.patch(`/missions/${missionId}`, data);
    return response.data;
  },

  delete: async (missionId: string) => {
    const response = await api.delete(`/missions/${missionId}`);
    return response.data;
  },

  validate: async (missionId: string) => {
    const response = await api.post(`/missions/${missionId}/validate`);
    return response.data;
  },

  approve: async (missionId: string) => {
    const response = await api.post(`/missions/${missionId}/approve`);
    return response.data;
  },

  submit: async (missionId: string) => {
    const response = await api.post(`/missions/${missionId}/submit`);
    return response.data;
  },

  start: async (missionId: string) => {
    const response = await api.post(`/missions/${missionId}/start`);
    return response.data;
  },

  complete: async (missionId: string) => {
    const response = await api.post(`/missions/${missionId}/complete`);
    return response.data;
  },

  export: async (missionId: string, format: 'JSON' | 'KML' | 'COT') => {
    const response = await api.get(`/missions/${missionId}/export`, {
      params: { format },
      responseType: format === 'JSON' ? 'json' : 'text',
    });
    return response.data;
  },
};

// ============================================================================
// GEOFENCES API
// ============================================================================

export const geofencesApi = {
  list: async (params?: { isActive?: boolean; type?: string }) => {
    const response = await api.get('/geofences', { params });
    return response.data;
  },

  get: async (geofenceId: string) => {
    const response = await api.get(`/geofences/${geofenceId}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    description?: string;
    type: string;
    // Either polygon `geometry` (GeoJSON) or the circle triplet
    // `centerLat` + `centerLng` + `radiusMeters` is required server-side.
    geometry?: unknown;
    centerLat?: number;
    centerLng?: number;
    radiusMeters?: number;
    altitudeMin?: number;
    altitudeMax?: number;
    alertSeverity?: string;
    notifyOnEntry?: boolean;
    notifyOnExit?: boolean;
    isActive?: boolean;
  }) => {
    const response = await api.post('/geofences', data);
    return response.data;
  },

  update: async (geofenceId: string, data: unknown) => {
    const response = await api.patch(`/geofences/${geofenceId}`, data);
    return response.data;
  },

  delete: async (geofenceId: string) => {
    const response = await api.delete(`/geofences/${geofenceId}`);
    return response.data;
  },
};

// ============================================================================
// SENSORS API
// ============================================================================

export const sensorsApi = {
  list: async (options?: { pageSize?: number; status?: string; type?: string }) => {
    const response = await api.get('/sensors', { params: options });
    return response.data;
  },

  get: async (sensorId: string) => {
    const response = await api.get(`/sensors/${sensorId}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    type: string;
    dataFormat: string;
    location?: unknown;
    configuration?: unknown;
  }) => {
    const response = await api.post('/sensors', data);
    return response.data;
  },

  update: async (sensorId: string, data: unknown) => {
    const response = await api.patch(`/sensors/${sensorId}`, data);
    return response.data;
  },

  delete: async (sensorId: string) => {
    const response = await api.delete(`/sensors/${sensorId}`);
    return response.data;
  },

  test: async (sensorId: string) => {
    const response = await api.post(`/sensors/${sensorId}/test`);
    return response.data;
  },
};

// ============================================================================
// AIRSPACE API
// ============================================================================

export const airspaceApi = {
  query: async (params: {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
    types?: string[];
  }) => {
    const response = await api.get('/airspace', { params });
    return response.data;
  },

  getFacilities: async (params: {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
    type?: string;
  }) => {
    const response = await api.get('/airspace/facilities', { params });
    return response.data;
  },
};

// ============================================================================
// ADS-B SNAPSHOTS API
// ============================================================================

export const adsbSnapshotsApi = {
  getSnapshots: async (params: {
    startTime: string;
    endTime: string;
    trackId?: string;
    limit?: number;
  }) => {
    const response = await api.get('/adsb-snapshots', { params });
    return response.data;
  },

  getLatest: async () => {
    const response = await api.get('/adsb-snapshots/latest');
    return response.data;
  },
};

// ============================================================================
// ANALYTICS API
// ============================================================================

export const analyticsApi = {
  getReports: async () => {
    const response = await api.get('/analytics/reports');
    return response.data;
  },

  createReport: async (data: {
    type: string;
    name: string;
    parameters: {
      startDate: string;
      endDate: string;
      geofenceIds?: string[];
      sensorIds?: string[];
    };
  }) => {
    const response = await api.post('/analytics/reports', data);
    return response.data;
  },

  getHeatmap: async (params: {
    startTime: string;
    endTime: string;
    resolution?: number;
  }) => {
    const response = await api.get('/analytics/heatmap', { params });
    return response.data;
  },
};

// ============================================================================
// USERS API (Admin)
// ============================================================================

export const usersApi = {
  list: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  get: async (userId: string) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  create: async (data: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    password: string;
  }) => {
    const response = await api.post('/users', data);
    return response.data;
  },

  update: async (userId: string, data: unknown) => {
    const response = await api.patch(`/users/${userId}`, data);
    return response.data;
  },

  delete: async (userId: string) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
};

// ============================================================================
// API KEYS API
// ============================================================================

export const apiKeysApi = {
  list: async () => {
    const response = await api.get('/api-keys');
    return response.data;
  },

  create: async (data: {
    name: string;
    permissions: string[];
    rateLimit?: number;
    expiresAt?: string;
  }) => {
    const response = await api.post('/api-keys', data);
    return response.data;
  },

  revoke: async (apiKeyId: string) => {
    const response = await api.delete(`/api-keys/${apiKeyId}`);
    return response.data;
  },
};

// ============================================================================
// TENANTS API (Super Admin)
// ============================================================================

export const tenantsApi = {
  list: async (params?: {
    search?: string;
    status?: string;
    tier?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const response = await api.get('/tenants', { params });
    return response.data;
  },

  get: async (tenantId: string) => {
    const response = await api.get(`/tenants/${tenantId}`);
    return response.data;
  },

  provision: async (data: {
    tenant: {
      name: string;
      slug: string;
      domain?: string;
      subscriptionTier: string;
      settings?: Record<string, unknown>;
    };
    adminUser: {
      email: string;
      firstName: string;
      lastName: string;
      password: string;
    };
  }) => {
    const response = await api.post('/tenants', data);
    return response.data;
  },

  update: async (tenantId: string, data: Record<string, unknown>) => {
    const response = await api.patch(`/tenants/${tenantId}`, data);
    return response.data;
  },

  suspend: async (tenantId: string, reason: string) => {
    const response = await api.post(`/tenants/${tenantId}/suspend`, { reason });
    return response.data;
  },

  activate: async (tenantId: string) => {
    const response = await api.post(`/tenants/${tenantId}/activate`);
    return response.data;
  },

  delete: async (tenantId: string) => {
    const response = await api.delete(`/tenants/${tenantId}`);
    return response.data;
  },

  getUsers: async (tenantId: string) => {
    const response = await api.get(`/tenants/${tenantId}/users`);
    return response.data;
  },
};
